import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

// ---------------------------------------------------------------------------
// Tier-change toast helpers
// ---------------------------------------------------------------------------

const TIER_ORDER = { free: 0, pro: 1, premium: 2 };

function titleCase(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function TierToast({ toast }) {
  if (!toast) return null;

  const isUpgrade = toast.type === 'success';
  const bg = isUpgrade
    ? 'bg-[rgba(2,148,86,0.12)] border-[#029456] text-[#029456]'
    : 'bg-[rgba(107,114,128,0.10)] border-[#6b7280] text-[#6b7280] dark:text-[#94a3b8]';

  return (
    <div className="fixed top-4 right-4 z-[200] animate-fade-in">
      <div
        className={`rounded-lg border px-4 py-3 shadow-lg text-[14px] font-medium ${bg}`}
        role="status"
      >
        {toast.message}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuthProvider
// ---------------------------------------------------------------------------

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierToast, setTierToast] = useState(null);

  const location = useLocation();
  const knownTierRef = useRef(null);
  const userIdRef = useRef(null);

  async function fetchProfile(userId) {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      setProfile(data);
      return data;
    } catch {
      setProfile(null);
      return null;
    }
  }

  // --- Tier-change detection -------------------------------------------

  const checkTierChange = useCallback((newProfile) => {
    if (!newProfile) return;
    const newTier = newProfile.subscription_tier || 'free';
    const oldTier = knownTierRef.current;

    // First load — just record the tier, no toast
    if (oldTier === null) {
      knownTierRef.current = newTier;
      return;
    }

    if (newTier === oldTier) return;

    knownTierRef.current = newTier;

    if ((TIER_ORDER[newTier] ?? 0) > (TIER_ORDER[oldTier] ?? 0)) {
      setTierToast({
        type: 'success',
        message: `Welcome to ${titleCase(newTier)}! 🎉`,
      });
    } else {
      setTierToast({
        type: 'info',
        message: `You're now on the ${titleCase(newTier)} plan.`,
      });
    }
  }, []);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (!tierToast) return;
    const timer = setTimeout(() => setTierToast(null), 5000);
    return () => clearTimeout(timer);
  }, [tierToast]);

  // Refetch profile on visibilitychange (tab re-focus)
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible' && userIdRef.current) {
        fetchProfile(userIdRef.current).then((p) => checkTierChange(p));
      }
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [checkTierChange]);

  // Refetch profile on route change
  useEffect(() => {
    if (userIdRef.current) {
      fetchProfile(userIdRef.current).then((p) => checkTierChange(p));
    }
  }, [location.pathname, checkTierChange]);

  // --- Auth lifecycle --------------------------------------------------

  useEffect(() => {
    // Check existing session on mount
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        userIdRef.current = s.user.id;
        fetchProfile(s.user.id).then((p) => {
          knownTierRef.current = p?.subscription_tier || 'free';
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        userIdRef.current = s.user.id;
        fetchProfile(s.user.id).then((p) => checkTierChange(p));
      } else {
        setProfile(null);
        userIdRef.current = null;
        knownTierRef.current = null;
      }
    });

    return () => subscription.unsubscribe();
  }, [checkTierChange]);

  const displayName = profile?.display_name || (user?.email ? user.email.split('@')[0] : null);
  const isAdmin = profile?.is_admin === true;

  async function signUp(email, password) {
    const { data, error } = await supabase.auth.signUp({ email, password });
    return { data, error };
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  }

  async function signOut() {
    const { error } = await supabase.auth.signOut();
    return { error };
  }

  return (
    <AuthContext.Provider value={{ user, session, profile, displayName, isAdmin, loading, signUp, signIn, signOut, fetchProfile }}>
      {children}
      <TierToast toast={tierToast} onDismiss={() => setTierToast(null)} />
    </AuthContext.Provider>
  );
}
